import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { creditsAPI } from '../api';
import { CreditPurchaseRequest } from '../types/api';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocketMessage } from './useWebSocket';

// Query keys
const QUERY_KEYS = {
  credits: ['credits'] as const,
};

// Get credit information
export function useCredits() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Listen for WebSocket credit updates
  useWebSocketMessage('credits.updated', (data: { balance: number }) => {
    console.log('🔍 [useCredits] Received WebSocket credit update:', data);
    
    // Update the credits query data directly
    queryClient.setQueryData(QUERY_KEYS.credits, (oldData: any) => {
      if (oldData) {
        return { ...oldData, balance: data.balance };
      }
      return { balance: data.balance };
    });
  }, [queryClient]);

  return useQuery({
    queryKey: QUERY_KEYS.credits,
    queryFn: async () => {
      console.log('🔍 [useCredits] Fetching credits from API...');
      const response = await creditsAPI.getCredits();
      console.log('🔍 [useCredits] API response:', response);
      console.log('🔍 [useCredits] Balance from API:', response.data?.balance);
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: Infinity, // Never stale, updated via WebSocket
    // Removed: refetchInterval - no more polling needed
  });
}

// Purchase credits mutation
export function usePurchaseCredits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreditPurchaseRequest) => creditsAPI.purchaseCredits(data),
    onSuccess: (response) => {
      // Invalidate credits query to get fresh data
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.credits });

      return response.data;
    },
  });
}

// Manual refresh of user credits (useful after payments)
export function useRefreshCredits() {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const response = await creditsAPI.getCredits();
      if (response.success && response.data && user) {
        // Update user context with new balance (use concrete object, not updater fn)
        updateUser({
          ...user,
          creditsBalance: response.data.balance,
        });
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate credits query and let subscribers update
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.credits });
    },
  });
}
